import { Button, Table } from '@flos/react-ui';
import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import CoverageTableRow from '../CoverageTable/CoverageTableRow';
import { getDeductiblesText, processBodyTerms } from '../../utils/termsUtil';
import { useHippoText } from '../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import { FLAVOUR_NAME_MAP } from '../../constants/coverable';
import { type ProductName_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { useCallback } from 'react';
import usePageNavigation from '../../hooks/usePageNavigation';
import { getKeyByValue } from '../../utils/objectUtil';
import { excludedFlavoursCoveragesConfig } from '../../config/excludedFlavorsConfig';
import { IS_OPC_EDIT_AVAILABLE } from '../../pages/OpcEntryPage/config/OpcConfig';

const IncludedCoverages = ({
    includedCoverages,
    showCoverableDetailsUI,
    shouldRenderTitle = false,
    flavourName,
    coverableId
}: {
    shouldRenderTitle?: boolean;
    showCoverableDetailsUI: boolean;
    includedCoverages: CoverageDTO[] | undefined;
    flavourName: string;
    coverableId: string;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const { goToOPCEditCoveragePage } = usePageNavigation();
    const navigateToAdjustExistingCoveragePage = useCallback((): void => {
        if (businessFlow !== 'DA') {
            return;
        }
        const productNameInfo = getKeyByValue(
            FLAVOUR_NAME_MAP,
            flavourName.toLowerCase() ?? ''
        ) as ProductName_Top;

        goToOPCEditCoveragePage(productNameInfo, coverableId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessFlow, coverableId, flavourName]);
    const productName = getKeyByValue(FLAVOUR_NAME_MAP, flavourName.toLocaleLowerCase() ?? '');
    const shouldHideAddCoverageButton =
        productName === 'top_cho_insurance' ||
        excludedFlavoursCoveragesConfig.adjustCoverages.includes(flavourName);
    return (
        <div className="included-coverages-container">
            {shouldRenderTitle && (
                <div className="u-display-flex@xs u-flex-justify-between@xs">
                    <h4>{getHippoText('DSS-SHOPPING-BASKET-SELECTED-COVERAGES-TITLE')}</h4>
                    {businessFlow !== 'ONSA' &&
                        !shouldHideAddCoverageButton &&
                        IS_OPC_EDIT_AVAILABLE && (
                            <div className="u-display-inline-block">
                                <Button
                                    onClick={navigateToAdjustExistingCoveragePage}
                                    size="small"
                                    className="u-spacing-stack-sm"
                                >
                                    {getHippoText('DSS-SHOPPING-BASKET-ADJUST-COVERABLE')}
                                </Button>
                            </div>
                        )}
                </div>
            )}
            <Table mobileDirection={showCoverableDetailsUI ? 'horizontal' : 'vertical'}>
                <Table.Body>
                    {includedCoverages?.map((coverage) => {
                        const includedCoverageTerms = processBodyTerms(
                            coverage?.terms ?? [{}],
                            flavourName
                        );
                        const { mainDeductibles, secondaryDeductibles } = includedCoverageTerms;
                        const deductibleTerms = mainDeductibles.concat(secondaryDeductibles);
                        return (
                            <CoverageTableRow
                                coverage={coverage}
                                iconProps={
                                    !showCoverableDetailsUI
                                        ? {
                                              shape: 'circle-check',
                                              className: 'has-fill is-green'
                                          }
                                        : undefined
                                }
                                deductibleText={getDeductiblesText(
                                    coverage,
                                    mainDeductibles,
                                    secondaryDeductibles
                                )}
                                key={coverage.name}
                                deductibleTerms={deductibleTerms}
                                shouldAlignTextLeft={showCoverableDetailsUI}
                                showEarlierEffectiveDates={showCoverableDetailsUI}
                            />
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );
};

export default IncludedCoverages;
